/**
 * PasswordForm.tsx
 *
 * Password entry form for the `login-password` and `signup-password` screens.
 *
 * PROPS
 * -----
 * onSubmit(password: string)    — called on form submit
 * onBack()                      — called when "Use a different email" is clicked
 * identifier                    — displayed above the field ("Logging in as …")
 * forgotPasswordHref            — href for the "Forgot your password?" link
 * isLoading                     — disables submit while Auth0 processes
 * errorMessage                  — Auth0 "wrong password" or similar error text
 * alternateLabel                — text for the alternate-method link
 * onAlternate()                 — called when the alternate method link is clicked
 * isNewPassword                 — when true, shows two fields + complexity rules (signup)
 *
 * Auth0 / ACUL note
 * -----------------
 * On submit, the parent calls `loginPassword.login({ password })` or
 * `signupPassword.signup({ password, confirmPassword })`.
 */

import { useId, useState } from "react";

import { SignupPasswordMembers } from "@auth0/auth0-acul-react/types";
import { BackButton } from "./BackButton";
import { CustomInput } from "./CustomInput";

// ---------------------------------------------------------------------------
// Password complexity rules (signup mode)
// ---------------------------------------------------------------------------

interface Rule {
  id: string;
  label: string;
  test: (pw: string) => boolean;
}

const PW_RULES: Rule[] = [
  { id: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "number", label: "One number", test: (p) => /\d/.test(p) },
];

function PasswordRules({
  password,
  confirmPassword,
}: {
  password: string;
  confirmPassword: string;
}) {
  if (!password) return null;
  const allRules: (Rule & { match?: boolean })[] = [
    ...PW_RULES.map((r) => ({ ...r, match: r.test(password) })),
    {
      id: "match",
      label: "Passwords match",
      test: () => password === confirmPassword && confirmPassword.length > 0,
      match: password === confirmPassword && confirmPassword.length > 0,
    },
  ];
  return (
    <ul className="pw-rules" aria-live="polite">
      {allRules.map((r) => (
        <li key={r.id} data-met={r.match ? "true" : "false"}>
          <span className="pw-rule-icon" aria-hidden="true" />
          {r.label}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PasswordFormProps {
  signupPassword?: SignupPasswordMembers;
  onSubmit: ({
    password,
    confirmPassword,
    email,
    phoneNumber,
  }: {
    password: string;
    confirmPassword?: string;
    email?: string;
    phoneNumber?: string;
  }) => void;
  onBack?: () => void;
  identifier?: string;
  forgotPasswordHref?: string;
  isLoading?: boolean;
  errorMessage?: string;
  alternateLabel?: string;
  onAlternate?: () => void;
  isNewPassword?: boolean;
  submitLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PasswordForm({
  signupPassword,
  onSubmit,
  onBack,
  identifier,
  forgotPasswordHref,
  isLoading = false,
  errorMessage,
  alternateLabel,
  onAlternate,
  isNewPassword = false,
  submitLabel,
}: PasswordFormProps) {
  const screenData = signupPassword?.screen?.data;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const uid = useId();

  const resolvedSubmitLabel =
    submitLabel ?? (isNewPassword ? "Create account" : "Log in");

  const confirmError =
    isNewPassword && touched && confirm && password !== confirm
      ? "Passwords do not match."
      : undefined;

  const allRulesMet =
    !isNewPassword ||
    (PW_RULES.every((r) => r.test(password)) &&
      password === confirm &&
      confirm.length > 0);

  const canSubmit = !isLoading && password.length >= 1 && allRulesMet;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({
      password,
      confirmPassword: isNewPassword ? confirm : undefined,
      email: screenData?.email,
      phoneNumber: screenData?.phoneNumber,
    });
  };

  return (
    <form className="auth-view" onSubmit={handleSubmit} noValidate>
      {/* Identifier context line */}
      {identifier && (
        <div className="auth-code-prompt">
          {isNewPassword ? (
            "Last step — create a password for security."
          ) : (
            <>
              Enter the password for{" "}
              <span className="auth-code-target">{identifier}</span>.
            </>
          )}
        </div>
      )}

      {/* Auth0 error banner */}
      {errorMessage && (
        <div className="auth-error-banner" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="auth-fields">
        <CustomInput
          id={`${uid}-password`}
          label={isNewPassword ? "Create a password" : "Password"}
          type="password"
          autoComplete={isNewPassword ? "new-password" : "current-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {!isNewPassword && forgotPasswordHref && (
          <a href={forgotPasswordHref} className="auth-forgot">
            Forgot your password?
          </a>
        )}

        {isNewPassword && (
          <>
            <CustomInput
              id={`${uid}-confirm`}
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onBlur={() => setTouched(true)}
              error={confirmError}
              required
            />
            <PasswordRules password={password} confirmPassword={confirm} />
          </>
        )}
      </div>

      {/* Alternate method link (e.g. "Prefer a 6-digit code?") */}
      {alternateLabel && onAlternate && (
        <p className="auth-mode-switch">
          <button type="button" onClick={onAlternate}>
            {alternateLabel}
          </button>
        </p>
      )}

      <div className="auth-submit-block">
        <button type="submit" className="auth-btn" disabled={!canSubmit}>
          {isLoading ? "Please wait…" : resolvedSubmitLabel}
        </button>

        {onBack && (
          <BackButton onClick={onBack}>
            Use a different email or phone
          </BackButton>
        )}
      </div>
    </form>
  );
}

export default PasswordForm;
